import { useState, useCallback } from 'react';
import { FileText, Image, Trash2, Download, Eye } from 'lucide-react';
import { saveAs } from 'file-saver';
import type { Document } from '@/types/document';
import { PDFViewerModal } from '@/components/PDFViewer';

interface DocumentListProps {
  documents: Document[];
  onDelete: (documentId: string) => void;
}

export function DocumentList({ documents, onDelete }: DocumentListProps) {
  const [selectedPdf, setSelectedPdf] = useState<string | null>(null);
  const [pdfError, setPdfError] = useState<string | null>(null);

  const formatFileSize = (bytes: number) => {
    if (bytes === 0) return '0 Bytes';
    const k = 1024;
    const sizes = ['Bytes', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
  };

  const getFileIcon = (mimeType: string) => {
    if (mimeType.startsWith('image/')) {
      return <Image className="h-6 w-6 text-indigo-500" />;
    }
    return <FileText className="h-6 w-6 text-indigo-500" />;
  };

  const downloadFile = useCallback(async (document: Document) => {
    try {
      const response = await fetch(document.url);
      if (!response.ok) {
        throw new Error(`Failed to download file: ${response.statusText}`);
      }
      
      const blob = await response.blob();
      saveAs(blob, document.name);
    } catch (error) {
      console.error('Download error:', error instanceof Error ? error.message : 'Unknown error');
      alert('Failed to download file. Please try again.');
    }
  }, []);

  const handleDownload = useCallback((document: Document) => downloadFile(document), [downloadFile]);

  const handleViewPDF = useCallback(async (document: Document) => {
    if (document.mimeType === 'application/pdf') {
      setSelectedPdf(document.url);
      setPdfError(null);
    }
  }, []);

  return (
    <>
      <div className="space-y-4">
      {documents.map((document) => (
        <div
          key={document.id}
          className="flex items-center justify-between p-4 bg-white rounded-lg shadow-sm hover:shadow-md transition-shadow duration-200"
        >
          <div className="flex items-center space-x-4">
            {getFileIcon(document.mimeType)}
            <div>
              <h4 className="text-sm font-medium text-gray-900">{document.name}</h4>
              <div className="flex space-x-4 mt-1">
                <span className="text-xs text-gray-500">
                  {new Date(document.uploadDate).toLocaleDateString()}
                </span>
                <span className="text-xs text-gray-500">
                  {formatFileSize(document.size)}
                </span>
                <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-indigo-100 text-indigo-800">
                  {document.type.charAt(0).toUpperCase() + document.type.slice(1)}
                </span>
              </div>
            </div>
          </div>
          <div className="flex items-center space-x-2">
            {document.mimeType === 'application/pdf' && (
              <button
                type="button"
                onClick={() => handleViewPDF(document)}
                className="p-1 text-gray-500 hover:text-indigo-600"
                title="View PDF"
              >
                <Eye className="h-5 w-5" />
              </button>
            )}
            <button
              type="button"
              onClick={() => handleDownload(document)}
              className="p-1 text-gray-500 hover:text-indigo-600"
              title="Download"
            >
              <Download className="h-5 w-5" />
            </button>
            <button
              type="button"
              onClick={() => onDelete(document.id)}
              className="p-1 text-gray-500 hover:text-red-600"
              title="Delete"
            >
              <Trash2 className="h-5 w-5" />
            </button>
          </div>
        </div>
      ))}
      {selectedPdf && (
        <div className="fixed inset-0 bg-black/75 flex items-center justify-center z-50">
          <PDFViewerModal
            url={selectedPdf}
            onClose={() => setSelectedPdf(null)}
            error={pdfError}
          />
        </div>
      )}
      </div>
    </>
  );
}